import { authenticatedClient } from '../lib/client';
import { WIDGET_ENDPOINTS } from './widgets.utils';
import type { ApiResponse } from '../lib/types';
import type { 
    WidgetResponse, 
    CreateWidgetRequest,
    UpdateWidgetRequest,
    RegenerateSecretResponse
} from './widgets.types';

/**
 * Get all widgets for the current organization
 */
export const getWidgets = async (): Promise<ApiResponse<WidgetResponse[]>> => {
    try {
        const response = await authenticatedClient.get(WIDGET_ENDPOINTS.GET_ALL);
        // API returns array directly
        const widgets = response.data || [];
        return { data: widgets };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch widgets' };
    }
};

/**
 * Get a specific widget by ID
 */
export const getWidgetById = async (widgetId: string): Promise<ApiResponse<WidgetResponse>> => {
    try {
        const response = await authenticatedClient.get(WIDGET_ENDPOINTS.GET_BY_ID(widgetId));
        return { data: response.data };
    } catch (error: any) {
        return { 
            error: error.response?.data?.message || 
                   error.response?.data?.detail || 
                   'Failed to fetch widget' 
        };
    }
};

/**
 * Get widget by chatbot ID
 */
export const getWidgetByChatbotId = async (chatbotId: string): Promise<ApiResponse<WidgetResponse>> => {
    try {
        const response = await authenticatedClient.get(WIDGET_ENDPOINTS.GET_BY_CHATBOT_ID(chatbotId));
        return { data: response.data };
    } catch (error: any) {
        return { 
            error: error.response?.data?.message || 
                   error.response?.data?.detail || 
                   'Failed to fetch widget by chatbot ID' 
        };
    }
};

/**
 * Create a new widget
 */
export const createWidget = async (payload: CreateWidgetRequest): Promise<ApiResponse<WidgetResponse>> => {
    try {
        // Set defaults if not provided
        const requestPayload = {
            ...payload,
            allowedOrigins: payload.allowedOrigins || ['*'],
            isActive: payload.isActive !== undefined ? payload.isActive : true,
        };

        const response = await authenticatedClient.post(WIDGET_ENDPOINTS.CREATE, requestPayload);
        return { data: response.data };
    } catch (error: unknown) {
        if (error instanceof Error) {
            return { 
                error: (error as any).response?.data?.message || 
                       (error as any).response?.data?.detail || 
                       error.message ||
                       'Failed to create widget'
            };
        }
        return { error: 'Failed to create widget' };
    }
};

/**
 * Update an existing widget
 */
export const updateWidget = async (
    widgetId: string,
    payload: UpdateWidgetRequest
): Promise<ApiResponse<WidgetResponse>> => {
    try {
        const response = await authenticatedClient.patch(WIDGET_ENDPOINTS.UPDATE(widgetId), payload);
        return { data: response.data };
    } catch (error: any) {
        return { 
            error: error.response?.data?.detail || 
                  error.response?.data?.message || 
                  error.message || 
                  'Failed to update widget' 
        };
    }
};

/**
 * Delete a widget
 */
export const deleteWidget = async (widgetId: string): Promise<ApiResponse<{ message: string }>> => {
    try {
        const response = await authenticatedClient.delete(WIDGET_ENDPOINTS.DELETE(widgetId));
        return { data: response.data };
    } catch (error: any) {
        return { 
            error: error.response?.data?.message || 
                   error.response?.data?.detail || 
                   'Failed to delete widget' 
        };
    }
};

/**
 * Regenerate HMAC secret for a widget
 */
export const regenerateWidgetSecret = async (widgetId: string): Promise<ApiResponse<RegenerateSecretResponse>> => {
    try {
        const response = await authenticatedClient.post(WIDGET_ENDPOINTS.REGENERATE_SECRET(widgetId));
        return { data: response.data };
    } catch (error: any) {
        return { 
            error: error.response?.data?.message || 
                   error.response?.data?.detail || 
                   'Failed to regenerate widget secret' 
        };
    }
};

// Helper functions for widget management

/**
 * Toggle widget active status
 */
export const toggleWidgetStatus = async (
    widgetId: string, 
    isActive: boolean
): Promise<ApiResponse<WidgetResponse>> => {
    return updateWidget(widgetId, { isActive });
};

/**
 * Update widget UI configuration only
 */
export const updateWidgetUI = async (
    widgetId: string, 
    uiConfig: UpdateWidgetRequest['uiConfig']
): Promise<ApiResponse<WidgetResponse>> => {
    return updateWidget(widgetId, { uiConfig });
};

/**
 * Update widget voice configuration only
 */
export const updateWidgetVoice = async (
    widgetId: string, 
    voiceConfig: UpdateWidgetRequest['voiceConfig']
): Promise<ApiResponse<WidgetResponse>> => {
    return updateWidget(widgetId, { voiceConfig });
};

/**
 * Update widget allowed origins
 */
export const updateWidgetOrigins = async (
    widgetId: string, 
    allowedOrigins: string[]
): Promise<ApiResponse<WidgetResponse>> => {
    return updateWidget(widgetId, { allowedOrigins });
};
